import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { UserPlus } from "@phosphor-icons/react";

export function ClaroStaffInvites() {
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/users/claro-invites").then(({ data }) => setInvites(data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/users/claro-invites", { email, reason });
      toast.success("Invitation sent. Access starts once Claro staff accept.");
      setOpen(false); setEmail(""); setReason(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const cancel = async (id) => {
    try { await api.delete(`/users/claro-invites/${id}`); toast.success("Invitation cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const pending = invites.filter((i) => i.status === "pending");

  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3" data-testid="claro-staff-invites">
      <div className="text-sm">
        {pending.length > 0 ? (
          <ul className="space-y-1">
            {pending.map((i) => (
              <li key={i.id} className="flex flex-wrap items-center gap-2" data-testid={`claro-invite-${i.id}`}>
                <span className="border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">Pending</span>
                <span>Claro staff <b>{i.staff_name || i.email}</b> invited {i.created_at?.slice(0, 10)} — {i.reason}</span>
                <button onClick={() => cancel(i.id)} className="text-xs text-red-600 underline" data-testid={`claro-invite-cancel-${i.id}`}>Cancel</button>
              </li>
            ))}
          </ul>
        ) : <span className="text-muted-foreground text-xs">Need help from Claro Compliance? Invite a Claro staff member; they get access only after accepting, and you can remove it anytime.</span>}
      </div>
      <Button variant="outline" className="rounded-none" onClick={() => setOpen(true)} data-testid="claro-invite-open"><UserPlus size={15} className="mr-1.5" />Invite Claro staff</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>Invite Claro staff to this entity</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input type="email" placeholder="name@clarocompliance.net" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none" data-testid="claro-invite-email" />
            <Textarea rows={3} placeholder="Reason you need assistance (required)" value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-none" data-testid="claro-invite-reason" />
            <p className="text-xs text-muted-foreground">They receive an email and must accept in ClaroConsole before they can open this entity. Every step is recorded in the Access Report.</p>
            <Button className="rounded-none w-full" disabled={busy || !email.trim() || !reason.trim()} onClick={send} data-testid="claro-invite-send">{busy ? "Sending…" : "Send invitation"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
